// src/Components/Modules/CRM/CrmDashboard.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton,
  Card, CardContent, LinearProgress, Tooltip, Skeleton,
  Tabs, Tab, Divider, Stack, Avatar
} from "@mui/material";
import {
  FaUsers, FaHandshake, FaChartLine, FaBullseye,
  FaPhone, FaEnvelope, FaCalendarAlt, FaStickyNote,
  FaTrophy, FaArrowUp, FaArrowDown, FaFilter, FaSync
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip } from "recharts";
import Swal from "sweetalert2";

const HeroCard = styled(Paper)(({ theme, gradient }) => ({
  background: gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
}));

const COLORS = ["#667eea", "#764ba2", "#f093fb", "#4facfe", "#43e97b", "#fa709a"];

const CrmDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("crm/dashboard", { params: { period } });
      setData(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load CRM data", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "৳0";
    return "৳" + Number(value).toLocaleString("en-BD", { maximumFractionDigits: 0 });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumb title="CRM Dashboard" />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const { lead_stats, deal_stats, activity_stats, recent_activities, top_deals, pipeline_distribution } = data;

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="CRM Dashboard" />

      {/* Header */}
      <HeroCard elevation={0} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaChartLine style={{ marginRight: 10, verticalAlign: "middle" }} />
              CRM Dashboard
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track your sales pipeline and customer relationships
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {["week", "month", "quarter", "year"].map((p) => (
              <Button
                key={p}
                size="small"
                variant={period === p ? "contained" : "outlined"}
                onClick={() => setPeriod(p)}
                sx={{
                  textTransform: "capitalize",
                  bgcolor: period === p ? "white" : "transparent",
                  color: period === p ? "#667eea" : "white",
                  borderColor: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                {p}
              </Button>
            ))}
          </Stack>
        </Box>
      </HeroCard>

      {/* Lead Stats */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={3}>
          <StatCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1.5, bgcolor: "#e3f2fd", borderRadius: 2, color: "#1976d2" }}>
                <FaUsers size={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Leads</Typography>
                <Typography variant="h4" fontWeight={700}>{lead_stats.total}</Typography>
                <Typography variant="caption" color="success.main">
                  <FaArrowUp size={10} /> {lead_stats.new_this_period} new
                </Typography>
              </Box>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1.5, bgcolor: "#e8f5e9", borderRadius: 2, color: "#2e7d32" }}>
                <FaHandshake size={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                <Typography variant="h4" fontWeight={700}>{lead_stats.conversion_rate}%</Typography>
                <Typography variant="caption" color="text.secondary">
                  {lead_stats.converted_this_period} this period
                </Typography>
              </Box>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1.5, bgcolor: "#fff3e0", borderRadius: 2, color: "#ef6c00" }}>
                <FaBullseye size={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pipeline Value</Typography>
                <Typography variant="h4" fontWeight={700}>{formatCurrency(deal_stats.total_value)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {deal_stats.open} open deals
                </Typography>
              </Box>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1.5, bgcolor: "#f3e5f5", borderRadius: 2, color: "#7b1fa2" }}>
                <FaTrophy size={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Won Revenue</Typography>
                <Typography variant="h4" fontWeight={700}>{formatCurrency(deal_stats.won_value)}</Typography>
                <Typography variant="caption" color="success.main">
                  {deal_stats.won} deals won
                </Typography>
              </Box>
            </Box>
          </StatCard>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              <FaChartLine style={{ marginRight: 8 }} /> Pipeline Distribution
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {pipeline_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pipeline_distribution}
                    dataKey="count"
                    nameKey={(entry) => entry.stage?.name}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => entry.stage?.name}
                  >
                    {pipeline_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.stage?.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No deals in pipeline
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              <FaBullseye style={{ marginRight: 8 }} /> Activity Breakdown
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {activity_stats.by_type.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={activity_stats.by_type}>
                  <XAxis dataKey="type" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#667eea" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No activities yet
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activities & Top Deals */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                <FaCalendarAlt style={{ marginRight: 8 }} /> Recent Activities
              </Typography>
              <Button size="small" onClick={() => navigate("/crm/activities")}>View All</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {recent_activities.length > 0 ? (
              <Stack spacing={1.5}>
                {recent_activities.map((activity) => (
                  <Box key={activity.id} sx={{ display: "flex", gap: 1.5, p: 1.5, bgcolor: "#f8f9fa", borderRadius: 1.5 }}>
                    <Avatar sx={{ bgcolor: activity.type_color, width: 40, height: 40 }}>
                      {activity.type_icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{activity.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.user?.name} • {new Date(activity.created_at).toLocaleString()}
                      </Typography>
                      {activity.lead && (
                        <Chip size="small" label={`Lead: ${activity.lead.title}`} sx={{ mt: 0.5, mr: 0.5 }} />
                      )}
                      {activity.deal && (
                        <Chip size="small" label={`Deal: ${activity.deal.title}`} sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No recent activities
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                <FaTrophy style={{ marginRight: 8 }} /> Top Deals
              </Typography>
              <Button size="small" onClick={() => navigate("/crm/deals")}>View All</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {top_deals.length > 0 ? (
              <Stack spacing={1.5}>
                {top_deals.map((deal) => (
                  <Box key={deal.id} sx={{ p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{deal.title}</Typography>
                      <Typography variant="subtitle2" fontWeight={700} color="success.main">
                        {formatCurrency(deal.value)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {deal.customer?.name} • {deal.stage?.name}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={deal.probability}
                      sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: deal.stage?.color || "#667eea" } }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No open deals
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CrmDashboard;